'use client'

import React from 'react';
import { useEffect, useState } from "react";
import { SpacingType, sectionVariants } from '@/types';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { SectionWrapper } from '../components/SectionWraper';
import { EmptyResult } from '../components/EmptyResult';
import parse from 'html-react-parser';
import { cn, variantBackgroundColors } from '@/lib/utils';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

interface resourceData {
  title: string;
  content: string;
  link: string;
}

interface ResourceListingProps {  
  spacing?: SpacingType;
  darkMode?: boolean; 
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  subtitle?: string;
  resources?: resourceData[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const ResourceCard = ({ resource }: { resource: resourceData }) => {
  return (
    <Link href={resource.link} tabIndex={-1} className="grid grid-cols-1 group">
      <Card
        className="flex p-6 shadow-md border-none gap-0"
      >
        <CardHeader className="py-4 px-0 gap-0">
          <h6>
            {parse(resource.title)}
          </h6>
        </CardHeader>
        <CardContent className="p-0 flex-1">
          <div className="paragraph-base text-muted-foreground">{parse(resource.content)}</div>
        </CardContent>
        <CardFooter className="p-0 mt-15">
          <Button variant="outline" size="iconLg" grayBorder className="group-hover:bg-accent">
            <span className="material-symbols-outlined group-hover:text-accent-foreground">
              arrow_forward
            </span>                      
          </Button>
        </CardFooter>
      </Card>
    </Link>
  );
};

export function ResourceListing({ 
  spacing = 'xlarge', 
  darkMode = false, 
  variant = 'A',  
  usedInPageNav = false, 
  pageNavTitle = 'Resources', 
  themeChangeOnScroll = false, 
  title, 
  subtitle,
  resources,
  attributes 
}: ResourceListingProps) {
  const [filteredResources, setFilteredResources] = useState<resourceData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter industries based on search term
  useEffect(() => {
    if (!resources) {
      setFilteredResources([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredResources(resources);
      return;
    }

    const filtered = resources.filter((resource) => {
      const titleMatch = resource.title.toLowerCase().includes(searchTerm.toLowerCase());
      const contentMatch = resource.content.toLowerCase().includes(searchTerm.toLowerCase());
      return titleMatch || contentMatch;
    });

    setFilteredResources(filtered);
  }, [searchTerm, resources]);

  // Reset search function
  const resetSearch = () => {
    setSearchTerm("");
  };

  const inputBackgroundColor = variantBackgroundColors(darkMode, variant);

  return (
    <>
      <SectionWrapper
        usedInPageNav={usedInPageNav}
        pageNavTitle={pageNavTitle}
        themeChangeOnScroll={themeChangeOnScroll}
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={false}
        componentName="resources-list"
        attributes={attributes}
      >
        <div className="container" data-aos="fade-up">
        <div className="flex flex-col md:flex-row gap-12 justify-between mb-6 md:mb-32 items-end">
          <div className="flex flex-col gap-6 w-full flex-1">
            <h2>
              {title}
            </h2>
            <div className="paragraph-lg text-muted-foreground">
              {subtitle}  
            </div>
          </div>
          <div className="relative w-full md:w-auto ">
            <Input 
              type="text" 
              placeholder="Search"  
              className={cn(inputBackgroundColor, "w-full md:w-80 pl-9")}
              value={searchTerm}  
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="material-symbols-outlined w-4.5 h-4.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ fontSize: '18px' }}>search</span>
          </div>
        </div>
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
            {filteredResources && filteredResources.length > 0 ? (
              filteredResources.map((item: resourceData, index: number) => {
                return (
                  <ResourceCard key={index} resource={item} />                 
                );
              })
            ) : (
              <div className="col-span-1 md:col-span-2 lg:col-span-3">
                <EmptyResult
                  className="mt-16"
                  buttonOnClick={resetSearch}
                />
              </div>
            )}
          </div>
        </div>
      </SectionWrapper>
    </>
  );
} 